import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Mail, Clock } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { supabase } from '../services/supabaseClient.ts';
import { handlePostLoginRedirect } from '../services/authRedirect.ts';

export default function AuthCallbackScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  console.log('[AuthCallbackScreen] Component mounted');

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const parseUrlParams = () => {
    // Parse both search params and hash params
    const urlSearchParams = Object.fromEntries(searchParams.entries());
    
    // Also check hash for tokens (common in OAuth flows)
    const hashParams: Record<string, string> = {};
    if (window.location.hash) {
      const hashString = window.location.hash.substring(1);
      const hashPairs = hashString.split('&');
      hashPairs.forEach(pair => {
        const [key, value] = pair.split('=');
        if (key && value) {
          hashParams[decodeURIComponent(key)] = decodeURIComponent(value);
        }
      });
    }

    // Merge both param sources, with search params taking precedence
    return { ...hashParams, ...urlSearchParams };
  };

  const handleResendEmail = async () => {
    setResending(true);
    setResendSuccess(false);
    
    try {
      console.log('[AuthCallbackScreen] Attempting to resend confirmation email');
      
      // Try to get the email from localStorage or prompt user
      const email = localStorage.getItem('pending_email_confirmation') || 
                   prompt('Please enter your email address to resend the confirmation:');
      
      if (!email) {
        setError('Email address is required to resend confirmation.');
        setResending(false);
        return;
      }

      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      });

      if (resendError) {
        console.error('[AuthCallbackScreen] Resend failed:', resendError);
        setError(`Failed to resend email: ${resendError.message}`);
      } else {
        console.log('[AuthCallbackScreen] Email resent successfully');
        setResendSuccess(true);
        setError(null);
        
        // Clear the error state after successful resend
        setTimeout(() => {
          setResendSuccess(false);
        }, 5000);
      }
    } catch (err) {
      console.error('[AuthCallbackScreen] Resend email error:', err);
      setError('Failed to resend confirmation email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleAuthCallback = async () => {
    try {
      console.log('[AuthCallbackScreen] Processing auth callback...');
      
      const allParams = parseUrlParams();
      console.log('[AuthCallbackScreen] All URL parameters:', allParams);
      console.log('[AuthCallbackScreen] Full URL:', window.location.href);

      // Get the auth code and error info from combined params
      const code = allParams.code;
      const error_code = allParams.error || allParams.error_code;
      const error_description = allParams.error_description;
      const type = allParams.type;
      const access_token = allParams.access_token;
      const refresh_token = allParams.refresh_token;
      
      console.log('[AuthCallbackScreen] Parsed Parameters:', {
        code: code ? `${code.substring(0, 10)}...` : null,
        error_code,
        error_description,
        type,
        hasAccessToken: !!access_token,
        hasRefreshToken: !!refresh_token
      });

      // Handle specific error cases
      if (error_code) {
        console.error('[AuthCallbackScreen] Auth error:', error_code, error_description);
        
        if (error_code === 'otp_expired' || error_description?.includes('expired')) {
          console.log('[AuthCallbackScreen] Detected expired link');
          setIsExpired(true);
          setError('Your email confirmation link has expired. Please request a new one.');
        } else {
          setError(error_description || `Authentication failed: ${error_code}`);
        }
        setLoading(false);
        return;
      }

      // If we have access_token and refresh_token in hash, handle OAuth flow
      if (access_token && refresh_token) {
        console.log('[AuthCallbackScreen] Found tokens in URL, setting session...');
        
        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token,
          refresh_token
        });

        if (sessionError) {
          console.error('[AuthCallbackScreen] Session setting failed:', sessionError);
          setError('Failed to establish session. Please try again.');
          setLoading(false);
          return;
        }

        if (data.session) {
          console.log('[AuthCallbackScreen] Session established successfully');
          setSuccess(true);
          setLoading(false);

          setTimeout(async () => {
            try {
              await handlePostLoginRedirect();
            } catch (redirectError) {
              console.error('[AuthCallbackScreen] Redirect failed:', redirectError);
              navigate('/upload');
            }
          }, 2000);
          return;
        }
      }

      // Handle code-based flow
      if (code) {
        console.log('[AuthCallbackScreen] Exchanging code for session...');

        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

        if (exchangeError) {
          console.error('[AuthCallbackScreen] Code exchange failed:', exchangeError);
          
          // Check if it's an expired code error
          if (exchangeError.message?.includes('expired') || exchangeError.message?.includes('invalid')) {
            setIsExpired(true);
            setError('Your email confirmation link has expired or is invalid. Please request a new one.');
          } else {
            setError(exchangeError.message || 'Failed to confirm email');
          }
          setLoading(false);
          return;
        }

        if (data.session) {
          console.log('[AuthCallbackScreen] Email confirmed successfully, user logged in');
          setSuccess(true);
          setLoading(false);

          // Clear any pending email confirmation
          localStorage.removeItem('pending_email_confirmation');

          setTimeout(async () => {
            try {
              await handlePostLoginRedirect();
            } catch (redirectError) {
              console.error('[AuthCallbackScreen] Redirect failed:', redirectError);
              navigate('/upload');
            }
          }, 2000);
          return;
        }
      }

      // Fallback: Check if user is already authenticated
      if (type === 'signup') {
        console.log('[AuthCallbackScreen] Detected signup type, checking current session');
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('[AuthCallbackScreen] Session check failed:', sessionError);
          setError('Failed to verify login status. Please try signing up again.');
          setLoading(false);
          return;
        }
        
        if (session) {
          console.log('[AuthCallbackScreen] Found existing session, proceeding as success');
          setSuccess(true);
          setLoading(false);
          
          setTimeout(async () => {
            try {
              await handlePostLoginRedirect();
            } catch (redirectError) {
              console.error('[AuthCallbackScreen] Redirect failed:', redirectError);
              navigate('/upload');
            }
          }, 2000);
          return;
        }
      }

      // Wait for auth state change (for cases where session is established asynchronously)
      console.log('[AuthCallbackScreen] Waiting for auth state change...');
      
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        console.log('[AuthCallbackScreen] Auth state changed:', event, !!session);
        
        if (event === 'SIGNED_IN' && session) {
          console.log('[AuthCallbackScreen] User signed in via auth state change');
          subscription.unsubscribe();
          setSuccess(true);
          setLoading(false);
          
          setTimeout(async () => {
            try {
              await handlePostLoginRedirect();
            } catch (redirectError) {
              console.error('[AuthCallbackScreen] Redirect failed:', redirectError);
              navigate('/upload');
            }
          }, 2000);
        }
      });

      // Set a timeout to prevent infinite waiting
      setTimeout(() => {
        subscription.unsubscribe();
        if (loading) {
          console.error('[AuthCallbackScreen] Timeout waiting for authentication');
          setError('No authentication code found. Please try signing up again.');
          setLoading(false);
        }
      }, 5000);

    } catch (err) {
      console.error('[AuthCallbackScreen] Unexpected error:', err);
      setError('An unexpected error occurred during email confirmation.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
        <Navbar />
        <main className="container mx-auto px-6 py-8 flex-1">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-8">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Confirming Your Email</h1>
              <p className="text-gray-600">Please wait while we verify your email address...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
        <Navbar />
        <main className="container mx-auto px-6 py-8 flex-1">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-8">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2 text-green-800">Email Confirmed!</h1>
              <p className="text-gray-600 mb-4">
                Your email has been successfully verified. You are now logged in.
              </p>
              <p className="text-sm text-gray-500">Redirecting you to the app...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      <Navbar />
      <main className="container mx-auto px-6 py-8 flex-1">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-8">
          <div className="text-center">
            {isExpired ? (
              <Clock className="w-12 h-12 text-orange-600 mx-auto mb-4" />
            ) : (
              <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-bold mb-2 text-red-800">
              {isExpired ? 'Link Expired' : 'Email Confirmation Failed'}
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            
            {resendSuccess && (
              <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-md">
                <p className="text-green-800 text-sm">
                  ✅ Confirmation email sent! Please check your inbox.
                </p>
              </div>
            )}
            
            <div className="space-y-3">
              {isExpired && (
                <button
                  onClick={handleResendEmail}
                  disabled={resending}
                  className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Resend Confirmation Email
                    </>
                  )}
                </button>
              )}
              <button
                onClick={() => navigate('/signup')}
                className="w-full py-2 px-4 bg-gray-600 text-white font-semibold rounded-md hover:bg-gray-700 transition-colors"
              >
                Try Signing Up Again
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2 px-4 bg-gray-500 text-white font-semibold rounded-md hover:bg-gray-600 transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
} 